// Single polling loop for the dashboard. Polls /api/snapshot every 5 seconds
// and fans the result out to consumers via useData(). Replaces the per-component
// polls that NodeStatus and JobManagement used to run.

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const POLL_INTERVAL_MS = 5000;
const SNAPSHOT_URL = '/api/snapshot';

const DEFAULT_DATA = {
  resources: [],
  clusters: {},
  network40g: {},
  nodeUsage: {},
  license: {},
  jobsLive: [],
  ts: 0,
  loading: true,
  isRefreshing: false,
  // Triggers an out-of-band fetch (e.g. user hitting the refresh button).
  refresh: () => {},
};

const DataContext = createContext(DEFAULT_DATA);

export function DataProvider({ children }) {
  const [state, setState] = useState({
    resources: [],
    clusters: {},
    network40g: {},
    nodeUsage: {},
    license: {},
    jobsLive: [],
    ts: 0,
    loading: true,
    isRefreshing: false,
  });

  const fetchSnapshot = useCallback(async ({ showSpinner = false } = {}) => {
    if (showSpinner) {
      setState(s => ({ ...s, isRefreshing: true }));
    }
    try {
      const res = await axios.get(SNAPSHOT_URL);
      const d = res.data || {};
      setState(s => ({
        ...s,
        resources: d.resources || [],
        clusters: d.clusters || {},
        network40g: d.network40g || {},
        nodeUsage: d.nodeUsage || {},
        license: d.license || {},
        jobsLive: d.jobs_live || [],
        ts: d.ts || 0,
        loading: false,
        isRefreshing: false,
      }));
    } catch (e) {
      // Don't blank the UI on a transient failure — keep the last good values.
      console.error('snapshot fetch:', e);
      setState(s => ({ ...s, loading: false, isRefreshing: false }));
    }
  }, []);

  useEffect(() => {
    fetchSnapshot();
    const id = setInterval(fetchSnapshot, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchSnapshot]);

  const refresh = useCallback(() => fetchSnapshot({ showSpinner: true }), [fetchSnapshot]);

  return (
    <DataContext.Provider value={{ ...state, refresh }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  return useContext(DataContext);
}
